import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { trackPageView } from "@/lib/api";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AuthCallback } from "@/components/AuthCallback";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import Salotto from "@/pages/Salotto";
import Services from "@/pages/Services";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import CourseProduct from "@/pages/CourseProduct";
import Checklist from "@/pages/Checklist";
import NotesWelcome from "@/pages/NotesWelcome";
import GuideLanding from "@/pages/GuideLanding";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ActivateAccount from "@/pages/ActivateAccount";
import Account from "@/pages/Account";
import Learn from "@/pages/Learn";
import Becoming from "@/pages/Becoming";
import BecomingSales from "@/pages/BecomingSales";
import ResidenceServiceRecord from "@/pages/ResidenceServiceRecord";
import ToolResidence from "@/pages/ToolResidence";
import Admin from "@/pages/Admin";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import LegalPage from "@/pages/LegalPage";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

function PageViewTracker() {
  const { pathname } = useLocation();
  useEffect(() => { trackPageView(pathname); }, [pathname]);
  return null;
}

function SmoothScroll() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Disable momentum scroll on app-style pages (sticky sidebars behave better natively)
    if (pathname.startsWith("/learn") || pathname.startsWith("/admin")) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true, lerp: 0.09 });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, [pathname]);
  return null;
}

function Chrome() {
  const { pathname, hash } = useLocation();
  const hideFooter = ["/login"].includes(pathname) || pathname.startsWith("/learn") || pathname.startsWith("/becoming") || pathname.startsWith("/admin") || pathname.startsWith("/payment");
  // Google sign-in return: exchange the session_id before any route/guard runs.
  if (hash?.includes("session_id=")) {
    return (<><Header /><main><AuthCallback /></main></>);
  }
  return (
    <>
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/salotto-digitale" element={<Salotto />} />
          <Route path="/salotto-digitale/becoming" element={<BecomingSales />} />
          <Route path="/salotto-digitale/residence-service-record" element={<ResidenceServiceRecord />} />
          <Route path="/tool/residence-service-record" element={<ToolResidence />} />
          <Route path="/course/:courseId" element={<CourseProduct />} />
          <Route path="/checklist" element={<Checklist />} />
          <Route path="/notes/welcome" element={<NotesWelcome />} />
          <Route path="/guide/is-estate-management-for-me" element={<GuideLanding />} />
          <Route path="/services" element={<Services />} />
          <Route path="/about" element={<About />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/activate" element={<ActivateAccount />} />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/learn/:courseId" element={<ProtectedRoute><Learn /></ProtectedRoute>} />
          <Route path="/becoming/*" element={<ProtectedRoute><Becoming /></ProtectedRoute>} />
          <Route path="/becoming-preview" element={<ProtectedRoute><BecomingSales /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute admin><Admin /></ProtectedRoute>} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/privacy" element={<LegalPage doc="privacy" />} />
          <Route path="/terms" element={<LegalPage doc="terms" />} />
          <Route path="/refund-policy" element={<LegalPage doc="refund" />} />
        </Routes>
      </main>
      {!hideFooter && <Footer />}
    </>
  );
}

function App() {
  return (
    <div className="App bg-bg min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <SmoothScroll />
          <ScrollToTop />
          <PageViewTracker />
          <Chrome />
          <Toaster position="bottom-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
